import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import ScrollArrows from 'components/common/scroll-arrows';
import UnifiedComparisonTable from 'components/common/unified-comparison-table';
import TalkingPoint from 'components/layout/talking-point';
import DuctedQuoteColumn from 'components/quote-page/ducted-quote-column';
import RebatesModal from 'components/quote-page/modals/rebates-modal';
import SendQuoteModal from 'components/quote-page/modals/send-quote-modal';
import QuoteColumn from 'components/quote-page/quote-column';
import { AlpineCartProvider } from 'contexts/alpine-cart-context';
import { useFooterConfig } from 'contexts/footer-context';
import { comparisonFeatureDependencies } from 'data/constants';
import {
  AirHandlerProductType,
  ComparisonFeatureType,
  ComparisonTableDataType,
  SelectedSystemType
} from 'data/types';
import { useGetQuoteDataByInstanceQuery } from 'features/api/project-api';
import {
  setActiveTab,
  setGlobalComparisonFeatures,
  setQuoteIDsToSend
} from 'features/ui';
import {
  useHasQuotableSystems,
  useIsSystemQuoted
} from 'hooks/use-quoted-unique-ids';
import { useStepTipSequence } from 'hooks/use-step-tip-sequence';
import useSystemDesignBypassMode from 'hooks/use-system-design-bypass-mode';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { withoutComparisonRemovedSystems } from 'utils/comparison-removal';
import { getDuctedCoverage } from 'utils/ducted-coverage';
import { getQuoteBackTarget } from 'utils/project';
import { getQuoteSendButtonState } from 'utils/quote-send-button';
import { getQuoteTipSections } from 'utils/quote-tips';
import { shouldShowQuotedSnapshot } from 'utils/quoted-systems';
import { useScrollSync } from 'utils/scroll-sync';
import { isSystemDesignLocked } from 'utils/system-design-bypass';
import { warningModal } from 'utils/ui';

export default function QuotePage() {
  const dispatch = useAppDispatch();
  const {
    selectedSystems,
    selectedDuctedSystems,
    systemsToCompare,
    comparisonRemovedUniqueIds
  } = useAppSelector((state) => state.systems);
  const { airHandlerProducts, condenserProducts } = useAppSelector(
    (state) => state.data
  );
  const { projectDetails, instanceUniqueId } = useAppSelector(
    (state) => state.project
  );
  const { structureUniqueId } = useAppSelector((state) => state.structure);
  const { data: quotedSystemsData } = useGetQuoteDataByInstanceQuery(
    {
      instanceUniqueId: instanceUniqueId!,
      structureUniqueId: structureUniqueId!
    },
    { skip: !instanceUniqueId || !structureUniqueId }
  );
  const rebatesModalState = useState(false);
  const [, setRebatesModalOpen] = rebatesModalState;
  const [referenceTableIndex, setReferenceTableIndex] = useState<number | null>(
    null
  );
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const [, setOptionIndex] = useState<number | null>(null);
  const [quotesComparisonData, setQuotesComparisonData] = useState<
    ComparisonTableDataType[]
  >([]);
  const [activeQuoteTab, setActiveQuoteTab] = useState<'quotable' | 'quoted'>(
    'quotable'
  );
  const columnsRefObj = useRef<HTMLDivElement | null>(null);
  const [setColumnsSync, setComparisonSync] = useScrollSync();

  const columnsRef = useCallback(
    (node: HTMLDivElement | null) => {
      columnsRefObj.current = node;
      setColumnsSync(node);
    },
    [setColumnsSync]
  );

  const quotedSystemsFromDb = useMemo(
    () =>
      withoutComparisonRemovedSystems(
        (quotedSystemsData ?? []).map((row) => ({
          ...(row.quoteData as SelectedSystemType),
          quoteId: row.quoteId
        })),
        comparisonRemovedUniqueIds
      ),
    [quotedSystemsData, comparisonRemovedUniqueIds]
  );

  const isSystemQuoted = useIsSystemQuoted();
  const hasQuotableSystems = useHasQuotableSystems();
  const quotedSystems = useMemo(
    () =>
      quotedSystemsFromDb.filter((system) =>
        shouldShowQuotedSnapshot(
          system.uniqueId,
          selectedSystems,
          isSystemQuoted
        )
      ),
    [quotedSystemsFromDb, selectedSystems, isSystemQuoted]
  );

  const quotableFilteredSystems = useMemo(
    () => selectedSystems.filter((s) => !isSystemQuoted(s)),
    [selectedSystems, isSystemQuoted]
  );

  const allSystems =
    activeQuoteTab === 'quotable' ? quotableFilteredSystems : quotedSystems;

  const ductedColumnsForTab =
    activeQuoteTab === 'quotable' ? selectedDuctedSystems : [];

  const whatsIncluded = [
    'Product Warranty',
    'Free Warranty Processing',
    'Unlimited Lifetime Tech Support',
    '30 Day Money Back Guarantee',
    'Free Shipping'
  ];

  const handleQuoteColumnClick = (systemIndex: number) => {
    setReferenceTableIndex(systemIndex);
    setRebatesModalOpen(true);
  };

  useEffect(() => {
    dispatch(
      setQuoteIDsToSend(
        [
          ...quotableFilteredSystems.map((system) => system.uniqueId),
          ...selectedDuctedSystems.map((system) => system.id.toString())
        ].slice(0, 3)
      )
    );
  }, [activeQuoteTab]);

  const possibleComparisonFeatures: ComparisonFeatureType[] = [
    'Price',
    'Potential Rebates',
    'Price After Rebates',
    'Heating Coverage',
    'Cooling Coverage',
    'Average Efficiency',
    'Total Units to Install',
    'Indoor Units',
    'Indoor Visibility',
    'Outdoor Units',
    'Outdoor Visibility',
    'Installation Difficulty',
    'Amperage'
  ];

  const convertNullToUndefined = (value: any) =>
    value === null ? undefined : value;

  useEffect(() => {
    const ductedSystems =
      activeQuoteTab === 'quotable' ? selectedDuctedSystems : [];

    if (allSystems.length > 0 || ductedSystems.length > 0) {
      const allQuotesData = [
        ...allSystems.map((system) => ({
          identifier: system.uniqueId,
          price:
            system.price +
            system.quotableAccessories.reduce(
              (total, acc) => total + acc.price * acc.quantity,
              0
            ) +
            (projectDetails?.customerTaxExempt ? 0 : system.tax ?? 0),
          heatingScore: convertNullToUndefined(system.heatingScore),
          coolingScore: convertNullToUndefined(system.coolingScore),
          condenserCoverage: system.condenserCoverage,
          // Ducted CCB systems (AHA-6796): the comparison cells render the
          // design-load variant when present.
          ductedCoverage: getDuctedCoverage(system),
          averageSeer: convertNullToUndefined(system.averageSeer),
          totalUnitsToInstall:
            system.configuration.condensers.length +
            system.configuration.condensers.flatMap((cond) => cond.zones)
              .length +
            system.configuration.condensers
              .flatMap((cond) => cond.zones)
              .filter((z) => z.blowerProduct).length,
          totalIndoorUnits:
            system.configuration.condensers.flatMap((cond) => cond.zones)
              .length +
            system.configuration.condensers
              .flatMap((cond) => cond.zones)
              .filter((z) => z.blowerProduct).length,
          uniqueIndoorProducts: [
            ...new Map(
              system.configuration.condensers
                .flatMap((cond) => cond.zones)
                .flatMap((zone) =>
                  [zone.selectedProduct, zone.blowerProduct].filter(
                    (p): p is AirHandlerProductType => p != null
                  )
                )
                .map(
                  (product) =>
                    airHandlerProducts.find(
                      (p) => p.productId === product.productId
                    ) ?? product
                )
                .map((obj) => [obj.productId, obj])
            ).values()
          ],
          totalOutdoorUnits: system.configuration.condensers.length,
          uniqueOutdoorProducts: [
            ...new Map(
              system.configuration.condensers
                .map((cond) => cond.selectedProduct)
                .map(
                  (product) =>
                    condenserProducts.find(
                      (p) => p.productId === product.productId
                    ) ?? product
                )
                .map((obj) => [obj.productId, obj])
            ).values()
          ],
          totalRebates: convertNullToUndefined(system.totalRebates),
          indoorVisibility: convertNullToUndefined(system.indoorVisibility),
          outdoorVisibility: convertNullToUndefined(system.outdoorVisibility),
          installationDifficulty: convertNullToUndefined(
            system.installationDifficulty
          ),
          amperage: convertNullToUndefined(system.amperage)
        })),
        ...ductedSystems.map((system) => ({
          identifier: system.id.toString(),
          price:
            system.price +
            system.quotableAccessories.reduce(
              (total, acc) => total + acc.price * acc.quantity,
              0
            ) +
            (projectDetails?.customerTaxExempt ? 0 : system.tax ?? 0),
          heatingScore: convertNullToUndefined(system.heatingScore),
          coolingScore: convertNullToUndefined(system.coolingScore),
          condenserCoverage: [],
          averageSeer: convertNullToUndefined(system.seer ?? system.seer2),
          totalUnitsToInstall: system.equipment.length,
          totalIndoorUnits:
            system.config.condensers.flatMap((c) => c.zones).length + 1,
          uniqueIndoorProducts: [
            ...new Map(
              system.config.condensers
                .flatMap((cond) => cond.zones)
                .map((zone) => zone.selectedProduct)
                .filter((p) => p !== undefined)
                .map((product) =>
                  airHandlerProducts.find(
                    (p) => p.productId === product.productId
                  )
                )
                .filter((p) => p !== undefined)
                .map((obj) => [obj.productId, obj])
            ).values()
          ],
          totalOutdoorUnits: system.config.condensers.length,
          uniqueOutdoorProducts: [
            ...new Map(
              system.config.condensers
                .map((cond) => cond.selectedProduct)
                .filter((p) => p !== undefined)
                .map((product) =>
                  condenserProducts.find(
                    (p) => p.productId === product.productId
                  )
                )
                .filter((p) => p !== undefined)
                .map((obj) => [obj.productId, obj])
            ).values()
          ],
          totalRebates: convertNullToUndefined(system.totalRebates),
          indoorVisibility: undefined,
          outdoorVisibility: undefined,
          installationDifficulty: undefined,
          amperage: undefined
        }))
      ];

      const globalFeatures = possibleComparisonFeatures.filter((feature) => {
        return allQuotesData.some((quoteData) =>
          comparisonFeatureDependencies[feature].every((dep) => quoteData[dep])
        );
      });

      dispatch(setGlobalComparisonFeatures(globalFeatures));
      setQuotesComparisonData(allQuotesData as ComparisonTableDataType[]);
    } else {
      setQuotesComparisonData([]);
    }
  }, [
    allSystems,
    selectedDuctedSystems,
    airHandlerProducts,
    condenserProducts,
    projectDetails,
    dispatch,
    activeQuoteTab
  ]);

  const { quoteIDsToSend } = useAppSelector((state) => state.ui);
  const sendQuoteModalOpenState = useState(false);
  const [sendQuoteModalOpen, setSendQuoteModalOpen] = sendQuoteModalOpenState;
  const quoteSendButton = getQuoteSendButtonState({
    selectedSystems,
    selectedDuctedSystems,
    quoteIDsToSend
  });

  const bypassMode = useSystemDesignBypassMode();

  const handleQuoteBack = useCallback(() => {
    dispatch(
      setActiveTab(
        getQuoteBackTarget(
          hasQuotableSystems ||
            selectedSystems.length > 0 ||
            selectedDuctedSystems.length > 0,
          systemsToCompare.length > 0,
          {
            systemDesignLocked: isSystemDesignLocked(bypassMode)
          }
        )
      )
    );
  }, [
    dispatch,
    hasQuotableSystems,
    selectedSystems.length,
    selectedDuctedSystems.length,
    systemsToCompare.length,
    bypassMode
  ]);

  const quoteButtonDisabled = quoteSendButton.disabled || sendQuoteModalOpen;

  const handleQuoteNext = useCallback(() => {
    if (quoteIDsToSend.length < 1) {
      warningModal('Select at least one quote to send');
      return;
    }
    if (quoteButtonDisabled) {
      if (!quoteSendButton.isSending && !sendQuoteModalOpen) {
        warningModal('Quote has already been sent to customer');
      }
      return;
    }
    setSendQuoteModalOpen(true);
  }, [
    quoteIDsToSend.length,
    quoteButtonDisabled,
    quoteSendButton.isSending,
    sendQuoteModalOpen,
    setSendQuoteModalOpen
  ]);

  useFooterConfig(
    useMemo(
      () => ({
        onBack: handleQuoteBack,
        onNext: handleQuoteNext,
        backText: 'Back',
        nextText: quoteSendButton.text,
        nextDisabled: quoteButtonDisabled,
        nextVariant: 'green' as const,
        showBackArrow: true
      }),
      [
        handleQuoteBack,
        handleQuoteNext,
        quoteSendButton.text,
        quoteButtonDisabled
      ]
    )
  );

  const textKeys = useAppSelector((state) => state.data.textKeys);
  const hasQuotableColumns =
    quotableFilteredSystems.length > 0 || selectedDuctedSystems.length > 0;
  const tipSections = useMemo(
    () =>
      getQuoteTipSections({
        textKeys,
        ready: activeQuoteTab === 'quotable' && hasQuotableColumns,
        hasSystemActions: quotableFilteredSystems.length > 0
      }),
    [
      textKeys,
      activeQuoteTab,
      hasQuotableColumns,
      quotableFilteredSystems.length
    ]
  );
  const { currentStepTipSequence, reopenTipSequence } = useStepTipSequence({
    sections: tipSections,
    currentSectionId: 'quote',
    currentStepComplete: false
  });

  const hasDuctedColumns =
    activeQuoteTab === 'quotable' && ductedColumnsForTab.length > 0;

  return (
    <AlpineCartProvider>
      <div className='mx-auto flex max-w-[1770px] flex-col gap-5'>
        <div className='flex items-center gap-2 text-3xl font-bold text-gray-900'>
          Get Your Quote
          {currentStepTipSequence && (
            <button
              type='button'
              className='relative flex items-center text-gray-400 hover:text-blue-500'
              onClick={reopenTipSequence}
              aria-label='Show page tips'
            >
              <QuestionMarkCircleIcon className='h-7 w-7' />
            </button>
          )}
        </div>
        <TalkingPoint>
          <div className='flex w-full flex-col gap-2'>
            <div className='font-semibold'>
              What&apos;s included with your system?
            </div>
            <ul className='flex w-full list-disc flex-wrap gap-3 pt-1'>
              {whatsIncluded.map((bullet) => (
                <li key={bullet} className='ml-5 w-1/4 min-w-[200px] pr-3'>
                  {bullet}
                </li>
              ))}
            </ul>
          </div>
        </TalkingPoint>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            className={clsx(
              'rounded-full px-4 py-2 text-sm font-semibold shadow ring-1 ring-black ring-opacity-5',
              activeQuoteTab === 'quotable'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            )}
            onClick={() => setActiveQuoteTab('quotable')}
          >
            Quotable Systems (
            {quotableFilteredSystems.length + selectedDuctedSystems.length})
          </button>
          <button
            type='button'
            className={clsx(
              'rounded-full px-4 py-2 text-sm font-semibold shadow ring-1 ring-black ring-opacity-5',
              activeQuoteTab === 'quoted'
                ? 'bg-blue-100 text-blue-700'
                : 'bg-white text-gray-700 hover:bg-gray-50'
            )}
            onClick={() => setActiveQuoteTab('quoted')}
          >
            Quoted Systems ({quotedSystems.length})
          </button>
        </div>
        {activeQuoteTab === 'quotable' &&
          quotableFilteredSystems.length === 0 &&
          selectedDuctedSystems.length === 0 && (
            <p className='w-full py-10 text-center text-gray-500'>
              No quotable systems.
            </p>
          )}
        {activeQuoteTab === 'quoted' && quotedSystems.length === 0 && (
          <p className='w-full py-10 text-center text-gray-500'>
            No quoted systems.
          </p>
        )}
        <div className='relative px-5'>
          <div
            key={activeQuoteTab}
            ref={columnsRef}
            className='hidden-scrollbar flex snap-x snap-mandatory gap-5 overflow-x-auto scroll-smooth'
          >
            {activeQuoteTab === 'quotable' &&
              ductedColumnsForTab.map((system, systemIndex: number | null) => (
                <div
                  key={system.id}
                  id={systemIndex === 0 ? 'first-quote-column' : undefined}
                  className='shrink-0 grow-0 basis-[calc((100%_-_2.5rem)_/_3)] snap-start'
                >
                  <DuctedQuoteColumn
                    system={system}
                    setRebatesModalOpen={setRebatesModalOpen}
                    optionIndex={systemIndex}
                    setOptionIndex={setOptionIndex}
                    systemIndex={systemIndex ?? 0}
                    handleQuoteColumnClick={handleQuoteColumnClick}
                  />
                </div>
              ))}
            {allSystems.map((system, systemIndex: number | null) => (
              <div
                key={system.id ?? system.uniqueId}
                id={
                  !hasDuctedColumns && systemIndex === 0
                    ? 'first-quote-column'
                    : undefined
                }
                className='shrink-0 grow-0 basis-[calc((100%_-_2.5rem)_/_3)] snap-start'
              >
                <QuoteColumn
                  system={system}
                  setRebatesModalOpen={setRebatesModalOpen}
                  optionIndex={systemIndex}
                  setOptionIndex={setOptionIndex}
                  systemIndex={systemIndex ?? 0}
                  handleQuoteColumnClick={handleQuoteColumnClick}
                  isQuoted={activeQuoteTab === 'quoted'}
                  quoteId={
                    activeQuoteTab === 'quoted'
                      ? (system as SelectedSystemType & { quoteId: string })
                          .quoteId
                      : undefined
                  }
                />
              </div>
            ))}
          </div>
          <ScrollArrows scrollRef={columnsRefObj} />
        </div>
        <UnifiedComparisonTable
          ref={setComparisonSync}
          data={quotesComparisonData}
          mode='quotes'
          showAdvanced={showMoreInfo}
          onToggleAdvanced={() => setShowMoreInfo((prev) => !prev)}
        />
        {referenceTableIndex !== null && (
          <RebatesModal
            openState={rebatesModalState}
            system={selectedSystems[referenceTableIndex]}
          />
        )}
      </div>
      <SendQuoteModal openState={sendQuoteModalOpenState} />
    </AlpineCartProvider>
  );
}
